import { Container, Label, NumericInput } from '@playcanvas/pcui';

import { Events } from '../events';

class TimeDisplay extends Container {
    private readonly events: Events;

    private readonly label: Label;

    private readonly popover: Container;

    private readonly input: NumericInput;

    private frameRate = 30;

    private timeOriginOffsetSec = 0;

    private currentFrame = 0;

    private popoverVisible = false;

    constructor(events: Events) {
        super({
            id: 'time-display'
        });

        this.events = events;

        this.dom.style.position = 'absolute';
        // Lower-left, offset upward to avoid overlapping the status bar.
        this.dom.style.left = '10px';
        this.dom.style.bottom = '42px';
        this.dom.style.top = '';
        this.dom.style.transform = '';
        this.dom.style.zIndex = '21';
        this.dom.style.pointerEvents = 'auto';

        this.label = new Label({
            class: 'time-display-label',
            text: 't = 0.00 s'
        });

        this.label.dom.style.cursor = 'pointer';
        this.label.dom.style.userSelect = 'none';
        this.label.dom.style.padding = '6px 10px';
        this.label.dom.style.borderRadius = '6px';
        this.label.dom.style.background = 'rgba(0,0,0,0.45)';
        this.label.dom.style.backdropFilter = 'blur(4px)';
        this.label.dom.style.color = 'white';
        this.label.dom.style.fontFamily = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';
        this.label.dom.style.fontSize = '14px';

        this.popover = new Container({
            class: 'time-display-popover'
        });
        this.popover.hidden = true;
        // When anchored near the bottom, open upward.
        this.popover.dom.style.position = 'absolute';
        this.popover.dom.style.left = '0';
        this.popover.dom.style.bottom = '100%';
        this.popover.dom.style.marginBottom = '6px';
        this.popover.dom.style.padding = '8px';
        this.popover.dom.style.borderRadius = '6px';
        this.popover.dom.style.background = 'rgba(0,0,0,0.55)';
        this.popover.dom.style.backdropFilter = 'blur(4px)';

        this.input = new NumericInput({
            value: 0,
            precision: 2,
            step: 0.1
        });

        this.input.dom.style.minWidth = '140px';

        this.input.on('change', (value: number) => {
            if (typeof value === 'number' && Number.isFinite(value)) {
                this.events.fire('timeline.setTimeOriginOffset', value);
            }
        });

        this.popover.append(this.input);

        this.append(this.label);
        this.append(this.popover);

        this.label.dom.addEventListener('click', (e) => {
            e.stopPropagation();
            this.togglePopover();
        });

        // Click-away to close.
        window.addEventListener('pointerdown', () => {
            if (this.popoverVisible) {
                this.setPopoverVisible(false);
            }
        });
        this.dom.addEventListener('pointerdown', e => e.stopPropagation());

        // Keep in sync with timeline.
        events.on('timeline.frameRate', (fps: number) => {
            this.frameRate = (typeof fps === 'number' && Number.isFinite(fps) && fps > 0) ? fps : 30;
            this.refresh();
        });
        events.on('timeline.timeOriginOffset', (offset: number) => {
            this.timeOriginOffsetSec = (typeof offset === 'number' && Number.isFinite(offset)) ? offset : 0;
            this.input.value = this.timeOriginOffsetSec;
            this.refresh();
        });
        events.on('timeline.frame', (frame: number) => {
            this.currentFrame = (typeof frame === 'number' && Number.isFinite(frame)) ? frame : 0;
            this.refresh();
        });
        events.on('timeline.time', (time: number) => {
            this.currentFrame = (typeof time === 'number' && Number.isFinite(time)) ? time : this.currentFrame;
            this.refresh();
        });

        // Initial values.
        try {
            const fps = events.invoke('timeline.frameRate') as number;
            this.frameRate = (typeof fps === 'number' && Number.isFinite(fps) && fps > 0) ? fps : 30;
        } catch {
            // ignore
        }
        try {
            const offset = events.invoke('timeline.timeOriginOffset') as number;
            this.timeOriginOffsetSec = (typeof offset === 'number' && Number.isFinite(offset)) ? offset : 0;
            this.input.value = this.timeOriginOffsetSec;
        } catch {
            // ignore
        }
        try {
            const frame = events.invoke('timeline.frame') as number;
            this.currentFrame = (typeof frame === 'number' && Number.isFinite(frame)) ? frame : 0;
        } catch {
            // ignore
        }

        this.refresh();
    }

    private setPopoverVisible(visible: boolean) {
        this.popoverVisible = visible;
        this.popover.hidden = !visible;
    }

    private togglePopover() {
        this.setPopoverVisible(!this.popoverVisible);
    }

    private refresh() {
        // Hide in empty scenes (best-effort) to avoid clutter.
        try {
            if ((this.events as any)?.functions?.has?.('scene.empty')) {
                const empty = this.events.invoke('scene.empty') as boolean;
                if (empty) {
                    this.hidden = true;
                    return;
                }
            }
        } catch {
            // best effort
        }

        this.hidden = false;

        const tSec = (this.currentFrame / this.frameRate) + this.timeOriginOffsetSec;
        const text = `t = ${tSec.toFixed(2)} s`;
        this.label.text = text;

        // Visual hint for t≈0.
        const nearZero = Math.abs(tSec) < 0.01;
        this.label.dom.style.boxShadow = nearZero ? '0 0 0 2px rgba(255,255,255,0.35) inset' : '';
    }
}

export { TimeDisplay };
